import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, Monitor, Smartphone, Tablet, Globe, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export const SEOAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchStats();
  }, [days]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('admin_token');
      
      const response = await axios.get(`${BACKEND_URL}/api/analytics/seo-stats?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching SEO stats:', error);
      toast.error("Erreur lors du chargement des statistiques");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!stats) {
    return <div className="text-center text-gray-500 py-12">Aucune donnée disponible</div>;
  }

  const deviceIcons = {
    desktop: Monitor,
    mobile: Smartphone,
    tablet: Tablet
  };

  return (
    <div className="space-y-6">
      {/* Header with Date Filter */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Rapports SEO & Analytics</h2>
          <p className="text-gray-600">Suivi du trafic et des sources de visiteurs</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value={7}>7 derniers jours</option>
          <option value={30}>30 derniers jours</option>
          <option value={90}>90 derniers jours</option>
        </select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <BarChart3 className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Total</span>
          </div>
          <div className="text-3xl font-bold mb-1">{stats.total_visits.toLocaleString()}</div>
          <div className="text-sm opacity-90">Visites totales</div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Uniques</span>
          </div>
          <div className="text-3xl font-bold mb-1">{stats.unique_visitors.toLocaleString()}</div>
          <div className="text-sm opacity-90">Visiteurs uniques</div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Globe className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Sources</span>
          </div>
          <div className="text-3xl font-bold mb-1">
            {Object.keys(stats.visits_by_referrer).length}
          </div>
          <div className="text-sm opacity-90">Sources de trafic</div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 opacity-80" />
            <span className="text-sm opacity-80">Moyenne</span>
          </div>
          <div className="text-3xl font-bold mb-1">
            {Math.round(stats.total_visits / days)}
          </div>
          <div className="text-sm opacity-90">Visites / jour</div>
        </div>
      </div>

      {/* Traffic Sources */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" />
            Sources de Trafic
          </h3>
          <div className="space-y-3">
            {Object.entries(stats.visits_by_referrer)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 10)
              .map(([referrer, count]) => {
                const percentage = ((count / stats.total_visits) * 100).toFixed(1);
                const isTargetSite = ['restobelgo.com', 'innova-pay.ca', 'lacuisineenrose.com'].includes(referrer);
                
                return (
                  <div key={referrer} className="space-y-1">
                    <div className="flex justify-between items-center">
                      <span className={`font-medium ${isTargetSite ? 'text-green-600' : 'text-gray-700'}`}>
                        {isTargetSite && '⭐ '}
                        {referrer}
                      </span>
                      <span className="text-sm text-gray-600">
                        {count} ({percentage}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${isTargetSite ? 'bg-green-500' : 'bg-blue-500'}`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>

        {/* Device Breakdown */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Monitor className="w-5 h-5 text-purple-600" />
            Répartition par Appareil
          </h3>
          <div className="space-y-4">
            {Object.entries(stats.device_breakdown).map(([device, count]) => {
              const percentage = ((count / stats.total_visits) * 100).toFixed(1);
              const Icon = deviceIcons[device] || Monitor;
              const colors = {
                desktop: 'text-blue-600 bg-blue-100',
                mobile: 'text-green-600 bg-green-100',
                tablet: 'text-purple-600 bg-purple-100'
              };
              
              return (
                <div key={device} className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${colors[device] || 'bg-gray-100'}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <span className="font-medium capitalize">{device}</span>
                      <span className="text-sm text-gray-600">{count} ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${device === 'desktop' ? 'bg-blue-500' : device === 'mobile' ? 'bg-green-500' : 'bg-purple-500'}`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top Pages */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-orange-600" />
          Pages les Plus Visitées
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Page</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Visites</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">%</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(stats.top_pages).map(([page, count], index) => {
                const percentage = ((count / stats.total_visits) * 100).toFixed(1);
                return (
                  <tr key={page} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <span className="font-mono text-sm text-gray-700">{page}</span>
                    </td>
                    <td className="py-3 px-4 text-right font-semibold">{count}</td>
                    <td className="py-3 px-4 text-right text-gray-600">{percentage}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily Visits Chart (Simple bars) */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          Trafic Quotidien
        </h3>
        <div className="space-y-2">
          {Object.entries(stats.visits_by_day)
            .sort(([a], [b]) => new Date(a) - new Date(b))
            .slice(-14) // Last 14 days
            .map(([date, count]) => {
              const maxVisits = Math.max(...Object.values(stats.visits_by_day));
              const percentage = (count / maxVisits) * 100;
              
              return (
                <div key={date} className="flex items-center gap-4">
                  <span className="text-sm text-gray-600 w-24">{date}</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-8 relative">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-8 rounded-full flex items-center justify-end pr-3"
                      style={{ width: `${percentage}%` }}
                    >
                      <span className="text-white font-semibold text-sm">{count}</span>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};
